import * as Yup from "yup";
import VehicleModel from "../../models/masters/vehicle-model";
import { API_END_POINT } from "../../constants";
import { InputField } from "../../components/form/input.field.component";
import { FormikModelDialog, SubmitStatus } from "../../core/formik-model-dialog.component";
import { CheckboxField, CheckBoxStyle } from "../../core/checkbox-field.component";
import { useEffect, useState } from "react";
import { useGetVehicleTypes } from "../../apiclient/master/useGetVehicleTypeApi";
import { useGetVehicleTransports } from "../../apiclient/master/useGetVehicleTransportApi";
import { SelectField } from "../../core/select-field.component";
import { Formik, Field } from 'formik';  // Import Formik and Field
import { useGetVehicles } from "../../apiclient/master/useGetVehicleApi";

interface VehicleEditProps {
  data: VehicleModel;
  isViewMode?: boolean; 
  onClose?: (status: SubmitStatus, data: any) => any;
}

const VehicleEntry: React.FC<VehicleEditProps> = ({ ...props }) => {
  let { data, onClose,isViewMode } = props;
  const title = data?.vehicleId > 0 ? "Edit Vehicle" : "Add Vehicle";

  // Initial form values
  let initialValue: VehicleModel = {
    vehicleId: data?.vehicleId || 0,
    code: data?.code || "",
    vehicleNumber: data?.vehicleNumber || "",
    driverName: data?.driverName || "",
    licenceNumber: data?.licenceNumber ||   "",
    driverMobile: data?.driverMobile || "",
    capacityInKg: data?.capacityInKg || 0,
    vehicleTransportId: data?.vehicleTransportId || 0,
    vehicleTransportName: data?.vehicleTransportName || "",
    vehicleTypeId: data?.vehicleTypeId || 0,
    vehicleTypeName: data?.vehicleTypeName || "",
    fcDocumentPath: data?.fcDocumentPath || "", // Make sure this is a string or empty string
    fcExpiryDate: data?.fcExpiryDate || "",
    insuranceDocumentPath: data?.insuranceDocumentPath || "",
    insuranceExpiryDate: data?.insuranceExpiryDate || "",
    contractStartFrom: data?.contractStartFrom || "",
    contractEndsOn: data?.contractEndsOn || "",
    trayCapacity: data?.trayCapacity || 0,
    isActive: data?.vehicleId > 0 ? data.isActive : true,
  };

  // Validation schema with added validation for licenceNumber
  const validationSchema = Yup.object().shape({
    code: Yup.string()
      .required("Required")
      .test("check-duplicate-code", "Code already exists", (value) => {
        return (
          value &&
          !existingCode.includes(value) ||
          (data?.code && value === data.code) // Allow current code during update
        );
      }),   
      
      
    });
  
  
  const [existingCode, setExistingCode] = useState<string[]>([]);
  const { data: fetchedVehicle } = useGetVehicles();  

  // State for select options
  const [vehicleTypeData, setVehicleTypeData] = useState<{ value: string; label: string }[]>([]);
  const [vehicleTransportData, setVehicleTransportData] = useState<{ value: string; label: string }[]>([]);

  const { data: fetchedVehicleType } = useGetVehicleTypes();
  const { data: fetchedVehicleTransport } = useGetVehicleTransports();

  // Fetch and map vehicle types and transport data  
  useEffect(() => {
      
    if (fetchedVehicle) {
      setExistingCode(fetchedVehicle.map((cat: any) => cat.code));
  }


    if (fetchedVehicleType && Array.isArray(fetchedVehicleType)) {
      const mappedVehicleType = fetchedVehicleType.map((vehicletype: any) => ({
        value: vehicletype.vehicleTypeId.toString(),  // Convert to string
        label: vehicletype.name,
      }));
      setVehicleTypeData(mappedVehicleType);
    }
    if (fetchedVehicleTransport && Array.isArray(fetchedVehicleTransport)) {
      const mappedVehicleTransport = fetchedVehicleTransport.map((vehicletransport: any) => ({
        value: vehicletransport.vehicleTransportId.toString(),  // Convert to string
        label: vehicletransport.name,
      }));
      setVehicleTransportData(mappedVehicleTransport);
    }
  }, [fetchedVehicleType, fetchedVehicleTransport,fetchedVehicle]);

  // Handle file selection for fcDocumentPath
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, field: string, setFieldValue: any) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      setFieldValue(field, file);  // Store file object (or path if you upload it)
    }
  };

  return (
    <Formik
      initialValues={initialValue}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // Handle the form submission logic here
        console.log(values);
      }}
    >
      {({ setFieldValue }) => (
        <FormikModelDialog
          width="70%"
          title={title}
          initialData={initialValue} // Pass the initialData here
          validationSchema={validationSchema}
          show={!!data}
          onClose={onClose}
          endpoint={API_END_POINT.MASTER}
          postRoute="Vehicle/SaveVehicle"
          showAddAnother={true}
          isViewOnly ={isViewMode}
          isUpDateEntityDetails={data?.vehicleId > 0 ? true : false}
        >
          <div>
            <div className="row">
              <div className="col-3">
                <InputField maxLength={10} label="Code *" name="code" />
              </div>

              <div className="col-4">
                <InputField maxLength={100} label="Vehicle Number" name="vehicleNumber" />
              </div>

              <div className="col-4">
                <InputField maxLength={100} label="Driver Name" name="driverName" />
              </div>

              <div className="col-4">
                <InputField maxLength={16} label="Licence Number" name="licenceNumber" />
              </div>

              <div className="col-4">
                <InputField maxLength={100} label="Driver Mobile" name="driverMobile" />
              </div>

              <div className="col-4">
                <InputField maxLength={100} label="Capacity In Kg" name="capacityInKg" />
              </div>

              <div className="col-6">
                <SelectField
                  label="Vehicle Transport Name"
                  name="vehicleTransportId"
                  options={vehicleTransportData}
                  //unSelectValue="0"  // Ensure unSelectValue is a string
                />
              </div>

              <div className="col-6">
                <SelectField
                  label="Vehicle Type Name"
                  name="vehicleTypeId"
                  options={vehicleTypeData}
                  //unSelectValue="0"  // Ensure unSelectValue is a string
                />
              </div>

              <div className="col-3">
                <InputField type="date" label="Fc Expiry Date" name="fcExpiryDate" />
              </div>

              <div className="col-3">
                <InputField type="date" label="Insurance Date" name="insuranceExpiryDate" />
              </div>

              <div className="col-3">
                <InputField type="date" label="Contract Start From" name="contractStartFrom" />
              </div>

              <div className="col-3">
                <InputField type="date" label="Contract Ends On" name="contractEndsOn" />
              </div>

              <div className="col-4">
                <InputField maxLength={100} label="Tray Capacity" name="trayCapacity" />
              </div>

              <div className="col-4">
                {/* File input for FC Document Path */}
                <label htmlFor="fcDocumentPath">FC Document Path</label>
                <input
                  type="file"
                  name="fcDocumentPath"
                  onChange={(event) => handleFileChange(event, "fcDocumentPath", setFieldValue)} 
                />
              </div>

              <div className="col-4">
                {/* File input for FC Document Path */}
                <label htmlFor="insuranceDocumentPath">Insurance Document Path</label>
                <input
                  type="file"
                  name="insuranceDocumentPath"
                  onChange={(event) => handleFileChange(event, "insuranceDocumentPath", setFieldValue)} 
                />
              </div>

              <div className="col-12">
                <CheckboxField isChecked={true} label="Is Active?" name="isActive" checkstyle={CheckBoxStyle.Normal} />
              </div>
            </div>
          </div>
        </FormikModelDialog>
      )}
    </Formik>
  );
};

export default VehicleEntry;
